import moment from "moment";
import { toast } from "react-toastify";
import {
	DEFAULT_FLIGHT_SORT,
	FLIGHT_BEST_SORT,
	FLIGHT_CHEAPEST_SORT,
	FLIGHT_DEPOSIT_SORT,
	FLIGHT_SEARCH_MIN_DAYS_FROM_TODAY,
	FLIGHT_WEEKLY_SORT,
} from "../../constants";
import { sendEventToGTM } from "../../sendEventToGTM";
import {
	getDayOfYear,
	getDurationDays,
	getEpochSeconds,
	getMonth,
	getWeekOfYear,
	getYear,
} from "../../utils/SearchUtils";
import { createAction, logAmplitudeEvent } from "../utils";
import { UPDATE_DETAIL_FLIGHT } from "./detailFlight";
import { sendBookingSearchStartedEvent } from "./sendBookingSearchStartedEvent";

const LOAD_URL_PARAMS_SEARCH = "plt-web/search/LOAD_URL_PARAMS_SEARCH";
const UPDATE_SEARCH = "plt-web/search/UPDATE_SEARCH";
const UPDATE_SEARCH_URL = "plt-web/search/UPDATE_SEARCH_URL";
const UPDATE_SEARCH_LOADING = "plt-web/search/UPDATE_SEARCH_LOADING";
const UPDATE_SEARCH_FLIGHTS = "plt-web/search/UPDATE_SEARCH_FLIGHTS";
const LOADING_SEARCH_RESULTS = "plt-web/search/LOADING_SEARCH_RESULTS";
const SET_CARRIERS_FILTER = "plt-web/search/SET_CARRIERS_FILTER";
const CLEAR_CARRIERS_FILTER = "plt-web/search/CLEAR_CARRIERS_FILTER";
const EMPTY_SEARCH_RESULTS = "plt-web/search/EMPTY_SEARCH_RESULTS";
const SET_CARRIERS_STOP_FILTER = "plt-web/search/SET_CARRIERS_STOP_FILTER";
const SET_CARRIERS_ARRIVAL_FILTER =
	"plt-web/search/SET_CARRIERS_ARRIVAL_FILTER";
const SET_CARRIERS_DEPARTURE_FILTER =
	"plt-web/search/SET_CARRIERS_DEPARTURE_FILTER";
const SET_SORT = "plt-web/search/SET_SORT";

const initialState = {
	query: {
		flights: {
			origin: null,
			destination: null,
			type: "return", // return or one-way
			departureDate: moment()
				.add(FLIGHT_SEARCH_MIN_DAYS_FROM_TODAY, "days")
				.format("YYYY-MM-DD"),
			returnDate: moment()
				.add(FLIGHT_SEARCH_MIN_DAYS_FROM_TODAY + 14, "days")
				.format("YYYY-MM-DD"),
			adults: 1,
			children: 0,
			infants: 0,
			cabinClass: "Y",
			multiCityLegs: [
				{
					origin: null,
					destination: null,
					departureDate: moment()
						.add(FLIGHT_SEARCH_MIN_DAYS_FROM_TODAY, "days")
						.format("YYYY-MM-DD"),
				},
				{
					origin: null,
					destination: null,
					departureDate: moment()
						.add(FLIGHT_SEARCH_MIN_DAYS_FROM_TODAY, "days")
						.format("YYYY-MM-DD"),
				},
			],
		},
	},
	results: {
		carrierDepartureFilter: [],
		carrierArrivalFilter: [],
		sort: DEFAULT_FLIGHT_SORT,
		data: [],
	},
	activeTab: 1, // 1 = flights, 2 = stays
	loading: false,
};

export default function reducer(state = initialState, action = {}) {
	switch (action.type) {
		case UPDATE_SEARCH_URL: {
			return {
				...state,
				url: action.payload,
			};
		}
		case UPDATE_SEARCH: {
			return {
				...state,
				query: {
					...state.query,
					flights: action.data,
				},
				activeTab: 1,
			};
		}
		case LOADING_SEARCH_RESULTS: {
			return {
				...state,
				results: {
					...state.results,
					data: [],
				},
				loading: true,
			};
		}
		case EMPTY_SEARCH_RESULTS: {
			return {
				...state,
				results: {
					data: [],
				},
			};
		}
		case UPDATE_SEARCH_LOADING: {
			return {
				...state,
				loading: true,
			};
		}
		case UPDATE_SEARCH_FLIGHTS: {
			if (action.payload.data === undefined) {
				return {
					...state,
					results: {
						data: [],
						errors: { message: "No results" },
					},
					loading: false,
				};
			}

			return {
				...state,
				results: {
					...state.results,
					data: action.payload.data,
					type: "flights",
					carriers: action.payload.dictionaries
						? action.payload.dictionaries.carriers
						: {},
					carriersVisibilityFilter: action.payload.carriersVisibilityFilter,
					hasAirlineCombinations: action.payload.hasAirlineCombinations,
				},
				loading: false,
			};
		}
		case SET_CARRIERS_FILTER: {
			return {
				...state,
				results: {
					...state.results,
					carriersVisibilityFilter: action.payload,
				},
			};
		}
		case CLEAR_CARRIERS_FILTER: {
			return {
				...state,
				results: {
					...state.results,
					carriersVisibilityFilter: [],
					carrierArrivalFilter: [],
					carrierDepartureFilter: [],
					carrierStopFilter: [],
				},
			};
		}
		case SET_CARRIERS_STOP_FILTER: {
			return {
				...state,
				results: {
					...state.results,
					carrierStopFilter: action.payload,
				},
			};
		}
		case SET_CARRIERS_DEPARTURE_FILTER: {
			return {
				...state,
				results: {
					...state.results,
					carrierDepartureFilter: action.payload,
				},
			};
		}
		case SET_CARRIERS_ARRIVAL_FILTER: {
			return {
				...state,
				results: {
					...state.results,
					carrierArrivalFilter: action.payload,
				},
			};
		}
		case SET_SORT: {
			return {
				...state,
				results: {
					...state.results,
					sort: action.payload,
				},
			};
		}
		case LOAD_URL_PARAMS_SEARCH: {
			const { url, carriers, stops, depart, arrive, sort, ...params } =
				action.params;

			return {
				...state,
				query: {
					...state.query,
					flights: {
						...state.query.flights,
						...params,
					},
				},
				results: {
					...state.results,
					carriersVisibilityFilter: carriers,
					carrierStopFilter: stops,
					carrierArrivalFilter: arrive,
					carrierDepartureFilter: depart,
					sort: sort ?? DEFAULT_FLIGHT_SORT,
				},
				url,
			};
		}
		case UPDATE_DETAIL_FLIGHT: {
			return {
				...state,
			};
		}
		default:
			return state;
	}
}

export const loadUrlParamsSearch = (params) => ({
	type: LOAD_URL_PARAMS_SEARCH,
	params,
});

export const updateSearch = (data, productType = null) => ({
	type: UPDATE_SEARCH,
	data,
	productType,
});

export const updateSearchLoading = () => ({
	type: LOADING_SEARCH_RESULTS,
});

export const emptySearchResults = (data = {}) => ({
	type: EMPTY_SEARCH_RESULTS,
});

export const buildAmplitudePayload = (searchQuery) => {
	if (!searchQuery) {
		return {};
	}
	return {
		"data.adults": searchQuery.adults,
		"data.cabinClass": searchQuery.cabinClass,
		"data.children": searchQuery.children,
		"data.currencyCode": searchQuery.currencyCode,
		"data.departureDate": searchQuery.departureDate,
		"data.departureDayOfYear": getDayOfYear(searchQuery.departureDate),
		"data.departureEpoch": getEpochSeconds(searchQuery.departureDate),
		"data.departureMonth": getMonth(searchQuery.departureDate),
		"data.departureWeekOfYear": getWeekOfYear(searchQuery.departureDate),
		"data.departureYear": getYear(searchQuery.departureDate),
		"data.destination.name": searchQuery.destination?.name,
		"data.destination.city": searchQuery.destination?.city,
		"data.destination.country": searchQuery.destination?.country,
		"data.destination.id": searchQuery.destination?.id,
		"data.infants": searchQuery.infants,
		"data.isFamily": searchQuery.children !== 0 || searchQuery.infants !== 0,
		"data.multiCityLegs": searchQuery.multiCityLegs
			? searchQuery.multiCityLegs.map((leg) => ({
					departureDate: leg.departureDate,
				}))
			: [],
		"data.origin.name": searchQuery.origin?.name,
		"data.origin.city": searchQuery.origin?.city,
		"data.origin.country": searchQuery.origin?.country,
		"data.origin.id": searchQuery.origin?.id,
		"data.returnDate": searchQuery.returnDate,
		"data.returnDayOfYear": getDayOfYear(searchQuery.returnDate),
		"data.returnEpoch": getEpochSeconds(searchQuery.returnDate),
		"data.returnMonth": getMonth(searchQuery.returnDate),
		"data.returnWeekOfYear": getWeekOfYear(searchQuery.returnDate),
		"data.returnYear": getYear(searchQuery.returnDate),
		"data.tripLength": getDurationDays(
			searchQuery.departureDate,
			searchQuery.returnDate,
		),
		"data.type": searchQuery.type,
	};
};

const toFlightOffersRequestBody = ({ searchQuery }) => {
	// build the parameters for flight search
	let params = {
		adults: searchQuery.adults,
		children: searchQuery.children,
		infants: searchQuery.infants,
		currencyCode: searchQuery.currencyCode,
		cabinClass: searchQuery.cabinClass,
		limit: 200,
		usingCredit: searchQuery.c === "t" ? true : undefined,
		source: "sabre",
	};

	const { multiCityLegs } = searchQuery;
	const multiCityQueries = [];

	if (searchQuery.type === "multi-city") {
		multiCityLegs.forEach((leg) => {
			multiCityQueries.push({
				origin: leg.origin.id,
				destination: leg.destination.id,
				departureDate: moment.utc(leg.departureDate).format("YYYY-MM-DD"),
			});
		});
		params = {
			...params,
			flights: multiCityQueries,
		};
	} else {
		params = {
			...params,
			flights: [
				{
					origin: searchQuery.origin.id,
					destination: searchQuery.destination.id,
					departureDate: searchQuery.departureDate,
				},
			],
		};
		if (searchQuery.returnDate) {
			params.flights.push({
				origin: searchQuery.destination.id,
				destination: searchQuery.origin.id,
				departureDate: searchQuery.returnDate,
			});
		}
	}

	logAmplitudeEvent("search", buildAmplitudePayload(searchQuery));

	return params;
};

let searchAbortController = null;
export const updateSearchFlights =
	(internalApi, searchQuery, searchResult) => (dispatch) => {
		if (searchAbortController) {
			searchAbortController.abort("new search initiated");
			searchAbortController = null;
		}
		searchAbortController = new AbortController();

		// cast ints
		searchQuery.adults = searchQuery.adults
			? Number.parseInt(searchQuery.adults)
			: 1;
		searchQuery.children = searchQuery.children
			? Number.parseInt(searchQuery.children)
			: 0;
		searchQuery.infants = searchQuery.infants
			? Number.parseInt(searchQuery.infants)
			: 0;

		dispatch(updateSearch(searchQuery));
		dispatch(updateSearchLoading());

		sendBookingSearchStartedEvent({ searchQuery });

		internalApi
			.post("/v2/flight-offers", toFlightOffersRequestBody({ searchQuery }), {
				signal: searchAbortController.signal,
			})
			.then((response) => {
				searchAbortController = null;

				const flights = response.data;
				const carriers = [];
				let hasAirlineCombinations = false;
				flights.forEach((flight) => {
					if (flight.carriers.length === 1) {
						carriers.push(flight.carriers[0]);
					} else if (flight.carriers.length > 1) {
						hasAirlineCombinations = true;
					}
				});
				const updatedCarriersArray = [...new Set(carriers)];
				const updatedCarriersDictionary = {};
				for (const key of updatedCarriersArray) {
					updatedCarriersDictionary[key] = response.dictionaries.carriers[key];
				}

				if (hasAirlineCombinations) {
					updatedCarriersArray.push("COMBINATIONS");
				}

				response.dictionaries.carriers = updatedCarriersDictionary;
				const availableCarrierCodes = Object.keys(updatedCarriersDictionary);
				const carriersVisibilityFilter =
					searchResult.carriersVisibilityFilter.filter((carrier) =>
						availableCarrierCodes.includes(carrier),
					);
				response.carriersVisibilityFilter = carriersVisibilityFilter;
				response.hasAirlineCombinations = hasAirlineCombinations;
				setUrlParam(
					"carriers",
					carriersVisibilityFilter,
					arrayToUrlParam({ COMBINATIONS: "XX" }),
				)(dispatch);
				dispatch(createAction(UPDATE_SEARCH_FLIGHTS, response));
			})
			.catch((err) => {
				if (err.name === "CanceledError") {
					return;
				}
				toast.error(err.response?.data?.error?.message || "An error occured.");
				dispatch(createAction(UPDATE_SEARCH_FLIGHTS, []));
			});
	};

const setUrlParam =
	(paramName, obj, serializer = arrayToUrlParam()) =>
	(dispatch) => {
		const url = new URL(window.location.href);
		const paramValue = serializer(obj);
		if (paramValue === "") {
			url.searchParams.delete(paramName);
		} else {
			url.searchParams.set(paramName, paramValue);
		}
		window.history.replaceState({}, "", url);

		dispatch(createAction(UPDATE_SEARCH_URL, url.pathname + url.search));
	};

export const updateCarriersFilter =
	(data = []) =>
	(dispatch) => {
		setUrlParam(
			"carriers",
			data,
			arrayToUrlParam({ COMBINATIONS: "XX" }),
		)(dispatch);
		dispatch(createAction(SET_CARRIERS_FILTER, data));
	};

export const clearCarriersFilter =
	(data = []) =>
	(dispatch) => {
		setUrlParam("carriers", "")(dispatch);
		dispatch(createAction(CLEAR_CARRIERS_FILTER, data));
	};

export const updateCarriersStopFilter =
	(data = []) =>
	(dispatch) => {
		setUrlParam("stops", data)(dispatch);
		dispatch(createAction(SET_CARRIERS_STOP_FILTER, data));
	};

export const updateCarriersArrivalFilter =
	(data = []) =>
	(dispatch) => {
		setUrlParam("arrive", data)(dispatch);
		dispatch(createAction(SET_CARRIERS_ARRIVAL_FILTER, data));
	};

export const updateCarriersDepartureFilter =
	(data = []) =>
	(dispatch) => {
		setUrlParam("depart", data)(dispatch);
		dispatch(createAction(SET_CARRIERS_DEPARTURE_FILTER, data));
	};

export const updateSort = (sort) => (dispatch) => {
	setUrlParam(
		"sort",
		sort,
		valueToUrlParam({
			[FLIGHT_BEST_SORT]: "B",
			[FLIGHT_CHEAPEST_SORT]: "C",
			[FLIGHT_DEPOSIT_SORT]: "D",
			[FLIGHT_WEEKLY_SORT]: "W",
		}),
	)(dispatch);
	dispatch(createAction(SET_SORT, sort, (a) => a));
};

function valueToUrlParam(transformations) {
	return (val) =>
		val === undefined || val === "" ? val : transformations[val];
}

function arrayToUrlParam(transformations = {}) {
	return (arr) =>
		arr
			? arr
					.map((x) => transformations[x] ?? x)
					.sort()
					.join("")
			: "";
}
